"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Mail, Clock, MessageSquare, Building2, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, ShieldCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "@/components/common/Link";
const useWvcLanguage = () => {
  const [lang, setLang] = useState<"en" | "zh" | "fr" | "es" | "de">("en");
  useEffect(() => {
    try {
      const stored = localStorage.getItem("wvc_lang");
      if (stored && ["en", "zh", "fr", "es", "de"].includes(stored)) {
        setLang(stored as "en" | "zh" | "fr" | "es" | "de");
      }
    } catch {}
  }, []);
  return lang;
};
const translations = {
  en: {
    customerService: "Customer service",
    personal: "Personal concierge and client care",
    reach: "Reach our styling advisors and client team for order support, bespoke sizing questions, or private consultation.",
    conciergeTitle: "Client concierge",
    conciergeDetail: "Email, phone, or messaging support available for order assistance and general inquiries.",
    hoursTitle: "Hours of operation",
    hoursValue: "Monday – Friday: 9am – 6pm EST",
    hoursDetail: "For urgent order issues, email our support team directly for priority assistance.",
    chatTitle: "Live styling chat",
    chatValue: "Typical response within 5 minutes",
    chatDetail: "Connect with a private styling consultant during business hours for real-time fit and fabric advice.",
    pressTitle: "Press & wholesale",
    pressDetail: "Media inquiries, editorial collaborations, and wholesale partnership requests, please contact our business development team.",
    directInquiries: "Direct inquiries",
    commitment: "Our service commitment",
    commitmentText: "Every inquiry receives prompt, individual consideration from our dedicated team. We never use automated responses for fit and fabric advice.",
    sendInquiry: "Send an inquiry",
    shareDetails: "Share your details below and our concierge will respond directly.",
    preparing: "Preparing concierge form...",
    notice: "Notice",
    retryLoading: "Retry loading form",
    sendAnother: "Send another inquiry",
    fullName: "Full name",
    fullNamePlaceholder: "E.g. Clara Dupont",
    email: "Email address",
    emailPlaceholder: "clara@example.com",
    orderNumber: "Order number",
    orderNumberPlaceholder: "Optional (e.g. YS-84920)",
    inquiryTopic: "Inquiry topic",
    sizing: "Sizing & fabric consultation",
    orderShipping: "Order & shipping inquiry",
    returnsExchanges: "Returns & garment exchanges",
    privateAppointments: "Private appointments & press",
    yourMessage: "Your message",
    messagePlaceholder: "Tell us how we can assist you...",
    sendMessage: "Send Message",
    sendingMessage: "Sending message..."
  },
  zh: {
    customerService: "客户服务",
    personal: "个人礼宾和客户关怀",
    reach: "联系我们的造型顾问和客户团队以获得订单支持、定制尺码问题或私人咨询。",
    conciergeTitle: "客户礼宾",
    conciergeDetail: "电子邮件、电话或消息支持可用于订单协助和常见问题。",
    hoursTitle: "营业时间",
    hoursValue: "周一至周五：美国东部时间上午9点至下午6点",
    hoursDetail: "如遇紧急订单问题，请直接向我们的支持团队发送电子邮件以获得优先帮助。",
    chatTitle: "实时造型聊天",
    chatValue: "通常在5分钟内回复",
    chatDetail: "在营业时间内与私人造型顾问联系，获得实时合身和织物建议。",
    pressTitle: "新闻和批发",
    pressDetail: "媒体咨询、编辑协作和批发合作请求，请联系我们的业务开发团队。",
    directInquiries: "直接查询",
    commitment: "我们的服务承诺",
    commitmentText: "每项咨询都会得到我们专业团队的及时个性化考虑。我们从不对合身和织物建议使用自动回复。",
    sendInquiry: "发送查询",
    shareDetails: "在下方分享您的详细信息，我们的礼宾将直接回复。",
    preparing: "准备礼宾表格...",
    notice: "通知",
    retryLoading: "重试加载表格",
    sendAnother: "发送另一个查询",
    fullName: "全名",
    fullNamePlaceholder: "例如 Clara Dupont",
    email: "电子邮件地址",
    emailPlaceholder: "clara@example.com",
    orderNumber: "订单号",
    orderNumberPlaceholder: "可选（例如YS-84920）",
    inquiryTopic: "查询主题",
    sizing: "尺码和织物咨询",
    orderShipping: "订单和运输查询",
    returnsExchanges: "退货和服装交换",
    privateAppointments: "私人任命和新闻",
    yourMessage: "你的信息",
    messagePlaceholder: "告诉我们我们如何可以协助您...",
    sendMessage: "发送消息",
    sendingMessage: "发送消息..."
  },
  fr: {
    customerService: "Service client",
    personal: "Concierge personnel et service à la clientèle",
    reach: "Contactez nos conseillers en style et notre équipe client pour le support de commande, les questions de dimensionnement sur mesure ou les consultations privées.",
    conciergeTitle: "Concierge client",
    conciergeDetail: "Support par courrier électronique, téléphone ou messagerie disponible pour l'assistance aux commandes et les demandes générales.",
    hoursTitle: "Heures d'ouverture",
    hoursValue: "Lundi – Vendredi: 9h – 18h EST",
    hoursDetail: "Pour les problèmes de commande urgents, envoyez un e-mail directement à notre équipe d'assistance pour une aide prioritaire.",
    chatTitle: "Chat de style en direct",
    chatValue: "Réponse typique dans les 5 minutes",
    chatDetail: "Connectez-vous avec un consultant en style privé pendant les heures d'ouverture pour des conseils sur l'ajustement et le tissu en temps réel.",
    pressTitle: "Presse et gros",
    pressDetail: "Pour les demandes médias, les collaborations éditoriales et les demandes de partenariat en gros, veuillez contacter notre équipe de développement commercial.",
    directInquiries: "Demandes Directes",
    commitment: "Notre engagement envers le service",
    commitmentText: "Chaque demande reçoit une considération rapide et individuelle de notre équipe dévouée. Nous n'utilisons jamais de réponses automatisées pour les conseils sur l'ajustement et le tissu.",
    sendInquiry: "Soumettre une Demande",
    shareDetails: "Partagez vos détails ci-dessous et notre concierge répondra directement.",
    preparing: "Préparation du formulaire de concierge...",
    notice: "Avis",
    retryLoading: "Réessayer de charger le formulaire",
    sendAnother: "Soumettre une autre demande",
    fullName: "Nom complet",
    fullNamePlaceholder: "P. ex. Clara Dupont",
    email: "Adresse email",
    emailPlaceholder: "clara@example.com",
    orderNumber: "Numéro de commande",
    orderNumberPlaceholder: "Optionnel (p. ex. YS-84920)",
    inquiryTopic: "Sujet de la demande",
    sizing: "Consultation sur la taille et le tissu",
    orderShipping: "Demande de commande et d'expédition",
    returnsExchanges: "Retours et échanges de vêtements",
    privateAppointments: "Rendez-vous privés et presse",
    yourMessage: "Votre message",
    messagePlaceholder: "Dites-nous comment nous pouvons vous aider...",
    sendMessage: "Envoyer un Message",
    sendingMessage: "Envoi du message..."
  },
  es: {
    customerService: "Servicio al cliente",
    personal: "Servicio personal de conserjería y cliente",
    reach: "Comuníquese con nuestros asesores de estilo y equipo de cliente para soporte de pedido, preguntas de dimensionamiento personalizado o consultas privadas.",
    conciergeTitle: "Concierge del cliente",
    conciergeDetail: "Soporte por correo electrónico, teléfono o mensajería disponible para asistencia de pedidos e consultas generales.",
    hoursTitle: "Horario de funcionamiento",
    hoursValue: "Lunes – Viernes: 9am – 6pm EST",
    hoursDetail: "Para problemas de pedido urgentes, envíe un correo electrónico a nuestro equipo de apoyo directamente para obtener asistencia prioritaria.",
    chatTitle: "Chat de estilo en vivo",
    chatValue: "Respuesta típica dentro de 5 minutos",
    chatDetail: "Conéctese con un consultor de estilo privado durante el horario de atención para consejos de ajuste y tejido en tiempo real.",
    pressTitle: "Prensa y mayorista",
    pressDetail: "Para consultas de medios, colaboraciones editoriales y solicitudes de asociación mayorista, póngase en contacto con nuestro equipo de desarrollo comercial.",
    directInquiries: "Consultas Directas",
    commitment: "Nuestro compromiso de servicio",
    commitmentText: "Cada consulta recibe consideración rápida e individual de nuestro equipo dedicado. Nunca utilizamos respuestas automatizadas para consejos de ajuste y tela.",
    sendInquiry: "Enviar una Consulta",
    shareDetails: "Comparte tus detalles a continuación y nuestro conserje responderá directamente.",
    preparing: "Preparando formulario de conserjería...",
    notice: "Aviso",
    retryLoading: "Reintentar cargar el formulario",
    sendAnother: "Enviar otra consulta",
    fullName: "Nombre completo",
    fullNamePlaceholder: "P. ej. Clara Dupont",
    email: "Dirección de correo electrónico",
    emailPlaceholder: "clara@example.com",
    orderNumber: "Número de orden",
    orderNumberPlaceholder: "Opcional (p. ej. YS-84920)",
    inquiryTopic: "Tema de la consulta",
    sizing: "Consulta de tamaño y tela",
    orderShipping: "Consulta de pedido y envío",
    returnsExchanges: "Devoluciones e intercambios de prendas",
    privateAppointments: "Citas privadas y prensa",
    yourMessage: "Tu mensaje",
    messagePlaceholder: "Cuéntanos cómo podemos ayudarte...",
    sendMessage: "Enviar Mensaje",
    sendingMessage: "Enviando mensaje..."
  },
  de: {
    customerService: "Kundenservice",
    personal: "Persönlicher Concierge und Kundenbetreuung",
    reach: "Wenden Sie sich an unsere Styling-Berater und unser Kundenteam für Bestellsupport, maßgeschneiderte Größenfragen oder private Beratung.",
    conciergeTitle: "Kundenkonzierge",
    conciergeDetail: "E-Mail-, Telefon- oder Messaging-Support für Bestellunterstützung und allgemeine Anfragen verfügbar.",
    hoursTitle: "Öffnungszeiten",
    hoursValue: "Montag – Freitag: 9 Uhr – 18 Uhr EST",
    hoursDetail: "Bei dringenden Bestellproblemen senden Sie eine E-Mail direkt an unser Support-Team für vorrangige Unterstützung.",
    chatTitle: "Live-Styling-Chat",
    chatValue: "Typische Antwort innerhalb von 5 Minuten",
    chatDetail: "Verbinden Sie sich mit einem privaten Styling-Berater während der Geschäftszeiten für Echtzeitberatung zu Sitz und Stoff.",
    pressTitle: "Presse und Großhandel",
    pressDetail: "Bitte wenden Sie sich für Medienanfragen, redaktionelle Zusammenarbeiten und Großhandelpartnerschaftsanfragen an unser Business Development Team.",
    directInquiries: "Direkte Anfragen",
    commitment: "Unser Serviceeinsatz",
    commitmentText: "Jede Anfrage erhält schnelle und individuelle Überlegungen von unserem engagierten Team. Wir verwenden niemals automatisierte Antworten für Sitz- und Stoffberatung.",
    sendInquiry: "Anfrage Senden",
    shareDetails: "Geben Sie Ihre Details unten an und unser Concierge antwortet direkt.",
    preparing: "Concierge-Formular wird vorbereitet...",
    notice: "Mitteilung",
    retryLoading: "Formular neu laden",
    sendAnother: "Weitere Anfrage Senden",
    fullName: "Vollständiger Name",
    fullNamePlaceholder: "Z. B. Clara Dupont",
    email: "E-Mail-Adresse",
    emailPlaceholder: "clara@example.com",
    orderNumber: "Bestellnummer",
    orderNumberPlaceholder: "Optional (z. B. YS-84920)",
    inquiryTopic: "Anfragethema",
    sizing: "Größen- und Stoffberatung",
    orderShipping: "Bestell- und Versandanfrage",
    returnsExchanges: "Rückgaben und Kleidungstausch",
    privateAppointments: "Private Termine und Presse",
    yourMessage: "Ihre Nachricht",
    messagePlaceholder: "Sagen Sie uns, wie wir Ihnen helfen können...",
    sendMessage: "Nachricht Senden",
    sendingMessage: "Nachricht wird gesendet..."
  }
};
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormFieldOption {
  value: string;
  label: string;
}
interface FormFieldDef {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  help?: string;
  required?: boolean;
  default?: string;
  options?: FormFieldOption[];
  config?: Record<string, any>;
  validation?: {
    messages?: {
      required?: string;
      invalid?: string;
      [key: string]: string | undefined;
    };
  };
}
interface FormDefinition {
  id?: number | string;
  key: string;
  name?: string;
  description?: string;
  availability?: {
    accepting: boolean;
    state: string;
    message: string;
  };
  fields: FormFieldDef[];
  submit?: {
    label?: string;
    processing_label?: string;
  };
  spam?: {
    token: string;
    honeypot?: {
      field: string;
    } | null;
    min_seconds?: number;
    challenge?: any;
  };
}
export default function Concierge() {
  const language = useWvcLanguage();
  const t = translations[language] || translations.en;
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [loadingForm, setLoadingForm] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [hpValue, setHpValue] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationHtml, setConfirmationHtml] = useState<string | null>(null);
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(0);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const loadForm = useCallback(async () => {
    setLoadingForm(true);
    setLoadError(null);
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      // Graceful fallback for design preview if pluginRequest global is uninitialized
      setFormDef({
        key: "contact_us",
        name: "Contact Concierge",
        availability: {
          accepting: true,
          state: "open",
          message: ""
        },
        fields: [{
          id: "f_name",
          key: "name",
          type: "text",
          label: t.fullName,
          placeholder: t.fullNamePlaceholder,
          required: true
        }, {
          id: "f_email",
          key: "email",
          type: "email",
          label: t.email,
          placeholder: t.emailPlaceholder,
          required: true
        }, {
          id: "f_order",
          key: "order_number",
          type: "text",
          label: t.orderNumber,
          placeholder: t.orderNumberPlaceholder,
          required: false
        }, {
          id: "f_topic",
          key: "topic",
          type: "select",
          label: t.inquiryTopic,
          required: true,
          options: [{
            value: "sizing_styling",
            label: t.sizing
          }, {
            value: "order_status",
            label: t.orderShipping
          }, {
            value: "returns_exchanges",
            label: t.returnsExchanges
          }, {
            value: "bespoke_press",
            label: t.privateAppointments
          }]
        }, {
          id: "f_message",
          key: "message",
          type: "textarea",
          label: t.yourMessage,
          placeholder: t.messagePlaceholder,
          required: true
        }],
        submit: {
          label: t.sendMessage,
          processing_label: t.sendingMessage
        },
        spam: {
          token: "preview_demo_token",
          min_seconds: 0,
          honeypot: null
        }
      });
      setFetchTimestamp(Date.now());
      setLoadingForm(false);
      return;
    }
    try {
      const def = await client.pluginRequest("wvc-forms/v1/forms/contact_us");
      if (!isMountedRef.current) return;
      setFormDef(def);
      setFetchTimestamp(Date.now());
      setLoadingForm(false);
    } catch (err: any) {
      if (err?.status === 404) {
        try {
          const listRes = await client.pluginRequest("wvc-forms/v1/forms");
          const items: FormDefinition[] = listRes?.items || [];
          const matched = items.find(i => i.key === "contact_us") || (items.length === 1 ? items[0] : null) || items.find(i => i.name?.toLowerCase().includes("contact") || i.name?.toLowerCase().includes("concierge"));
          if (matched && isMountedRef.current) {
            const resolved = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
            if (!isMountedRef.current) return;
            setFormDef(resolved);
            setFetchTimestamp(Date.now());
            setLoadingForm(false);
            return;
          }
        } catch {
          // Swallow recovery inner failure
        }
      }
      if (isMountedRef.current) {
        setLoadError("Unable to load the contact form at this moment. Please email our concierge directly.");
        setLoadingForm(false);
      }
    }
  }, [t]);
  useEffect(() => {
    loadForm();
  }, [loadForm]);
  const handleInputChange = (fieldId: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[fieldId];
        return next;
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || isSubmitting) return;

    // Check minimum duration rule for anti-spam
    const minSeconds = formDef.spam?.min_seconds ?? 3;
    const elapsedSeconds = (Date.now() - fetchTimestamp) / 1000;
    if (elapsedSeconds < minSeconds) {
      const waitTime = Math.ceil((minSeconds - elapsedSeconds) * 1000);
      await new Promise(resolve => setTimeout(resolve, Math.max(200, waitTime)));
    }
    setIsSubmitting(true);
    setGeneralError(null);
    setFieldErrors({});
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      // Simulate client-side demo if plugin is not connected
      await new Promise(resolve => setTimeout(resolve, 600));
      setConfirmationHtml("<p class='text-base font-sans leading-relaxed'>Thank you for reaching out. A client advisor from our London atelier will review your message and reply within 24 business hours.</p>");
      setIsSubmitting(false);
      return;
    }
    try {
      const payloadData: Record<string, any> = {
        values: formValues,
        token: formDef.spam?.token,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef.spam?.honeypot?.field) {
        payloadData.hp = {
          [formDef.spam.honeypot.field]: hpValue
        };
      }
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: payloadData
      });
      if (res.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
        } else {
          setConfirmationHtml(res.confirmation?.message || "<p class='text-base font-sans leading-relaxed'>Thank you. Your message has been safely delivered to our concierge team.</p>");
        }
      } else if (res.code === "frms_validation_failed") {
        setFieldErrors(res.errors || {});
        setGeneralError(res.message || "Please review the highlighted fields below.");
      } else if (res.code === "frms_invalid_token") {
        await loadForm();
        setGeneralError("Your session expired for security. Please try submitting once more.");
      } else {
        setGeneralError(res.message || "We could not process your submission. Please try again.");
      }
    } catch (err: any) {
      setGeneralError(err?.message || "A network error occurred. Please reach us via email.");
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };
  const contactChannels = [{
    icon: Mail,
    title: t.conciergeTitle,
    value: "care@yishengfushi.com",
    href: "mailto:care@yishengfushi.com",
    detail: t.conciergeDetail
  }, {
    icon: Clock,
    title: t.hoursTitle,
    value: t.hoursValue,
    detail: t.hoursDetail
  }, {
    icon: MessageSquare,
    title: t.chatTitle,
    value: t.chatValue,
    detail: t.chatDetail
  }, {
    icon: Building2,
    title: t.pressTitle,
    value: "press@yishengfushi.com",
    href: "mailto:press@yishengfushi.com",
    detail: t.pressDetail
  }];

  // Find fields matching the planned layout
  const visibleFields = formDef?.fields?.filter(f => !["heading", "paragraph", "hidden", "total", "payment"].includes(f.type)) || [];
  return <section id="联系我们" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="max-w-[1360px] mx-auto px-6 sm:px-10 lg:px-16">
        {/* Header Eyebrow and Headline */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <p className="text-[0.8125rem] font-sans font-semibold tracking-[0.12em] uppercase text-muted-foreground mb-3">
            {t.customerService}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground leading-[1.15] mb-4">
            {t.personal}
          </h2>
          <p className="font-sans text-base sm:text-lg font-light text-muted-foreground leading-relaxed">
            {t.reach}
          </p>
        </div>

        {/* 2-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Direct Channels */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-card p-8 border border-border">
              <h3 className="font-serif text-xl sm:text-2xl font-medium tracking-tight mb-6 pb-4 border-b border-border">
                {t.directInquiries}
              </h3>

              <div className="space-y-6">
                {contactChannels.map((channel, i) => {
                const Icon = channel.icon;
                return <div key={i} data-index={i} className="flex items-start gap-4">
                      <div className="size-10 shrink-0 bg-background border border-border flex items-center justify-center text-foreground mt-0.5">
                        <Icon className="size-4" strokeWidth={1.5} />
                      </div>
                      <div className="space-y-1 min-w-0">
                        <p className="text-xs font-sans font-semibold tracking-wider uppercase text-muted-foreground">
                          {channel.title}
                        </p>
                        {channel.href ? <Link to={channel.href} className="block font-sans text-sm sm:text-base font-medium text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline break-all">
                            {channel.value}
                          </Link> : <p className="font-sans text-sm sm:text-base font-medium text-foreground">
                            {channel.value}
                          </p>}
                        <p className="font-sans text-xs text-muted-foreground leading-normal">
                          {channel.detail}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            {/* Atelier Promise Card */}
            <div className="p-6 bg-background border border-border flex items-start gap-3.5">
              <ShieldCheck className="size-5 shrink-0 text-primary mt-0.5" strokeWidth={1.5} />
              <div className="space-y-1">
                <p className="text-xs font-sans font-semibold tracking-wider uppercase text-foreground">
                  {t.commitment}
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed font-sans">
                  {t.commitmentText}
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Dynamic Concierge Form */}
          <div className="lg:col-span-7 bg-card p-8 sm:p-10 border border-border">
            <div className="mb-6 pb-4 border-b border-border">
              <h3 className="font-serif text-2xl font-medium tracking-tight text-foreground">
                {t.sendInquiry}
              </h3>
              <p className="font-sans text-xs sm:text-sm text-muted-foreground mt-1">
                {t.shareDetails}
              </p>
            </div>

            {loadingForm ? <div className="py-16 text-center space-y-3">
                <div className="size-6 border-2 border-primary border-t-transparent animate-spin mx-auto" />
                <p className="text-xs text-muted-foreground font-sans">
                  {t.preparing}
                </p>
              </div> : loadError ? <div className="p-6 border border-destructive/30 bg-background text-foreground space-y-3">
                <div className="flex items-center gap-2 text-destructive font-medium text-sm">
                  <AlertCircle className="size-4 shrink-0" />
                  <span>{t.notice}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{loadError}</p>
                <Button variant="outline" size="sm" onClick={loadForm} className="mt-2 text-xs">
                  {t.retryLoading}
                </Button>
              </div> : confirmationHtml ? <div className="py-8 space-y-6">
                <div className="size-12 bg-primary/10 border border-primary/20 text-primary flex items-center justify-center">
                  <CheckCircle2 className="size-6" strokeWidth={1.5} />
                </div>
                <div className="prose prose-sm text-foreground space-y-3" dangerouslySetInnerHTML={{
              __html: confirmationHtml
            }} />
                <Button variant="outline" onClick={() => {
              setConfirmationHtml(null);
              setFormValues({});
            }} className="rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors text-xs uppercase tracking-wider font-semibold py-3 px-6 h-auto">
                  {t.sendAnother}
                </Button>
              </div> : formDef?.availability?.accepting === false ? <div className="p-6 border border-border bg-background text-muted-foreground text-sm">
                {formDef.availability.message || "Our concierge desk is currently closed for submissions."}
              </div> : <form onSubmit={handleSubmit} className="space-y-5">
                {/* Anti-spam honeypot decoy */}
                {formDef?.spam?.honeypot?.field && <div style={{
              position: "absolute",
              left: "-9999px",
              opacity: 0,
              pointerEvents: "none"
            }} aria-hidden="true">
                    <input type="text" name={formDef.spam.honeypot.field} value={hpValue} onChange={e => setHpValue(e.target.value)} tabIndex={-1} autoComplete="off" />
                  </div>}

                {generalError && <div className="p-3 bg-destructive/10 border border-destructive text-destructive text-xs flex items-center gap-2">
                    <AlertCircle className="size-4 shrink-0" />
                    <span>{generalError}</span>
                  </div>}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Name field */}
                  {visibleFields.filter(f => f.key.includes("name") || f.type === "text" && !f.key.includes("order")).slice(0, 1).map(field => <div key={field.id} className="space-y-1.5">
                        <label htmlFor={field.id} className="block text-xs font-sans font-medium uppercase tracking-wider text-foreground">
                          {field.label}
                          {field.required && <span className="text-primary ml-1">*</span>}
                        </label>
                        <Input id={field.id} type="text" required={field.required} placeholder={field.placeholder || t.fullNamePlaceholder} value={formValues[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} className="rounded-none bg-background border-input px-3.5 py-2.5 text-sm placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary" />
                        {fieldErrors[field.id] && <p className="text-xs text-destructive mt-1">
                            {fieldErrors[field.id].message}
                          </p>}
                      </div>)}

                  {/* Email field */}
                  {visibleFields.filter(f => f.type === "email" || f.key.includes("email")).slice(0, 1).map(field => <div key={field.id} className="space-y-1.5">
                        <label htmlFor={field.id} className="block text-xs font-sans font-medium uppercase tracking-wider text-foreground">
                          {field.label}
                          {field.required && <span className="text-primary ml-1">*</span>}
                        </label>
                        <Input id={field.id} type="email" required={field.required} placeholder={field.placeholder || t.emailPlaceholder} value={formValues[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} className="rounded-none bg-background border-input px-3.5 py-2.5 text-sm placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary" />
                        {fieldErrors[field.id] && <p className="text-xs text-destructive mt-1">
                            {fieldErrors[field.id].message}
                          </p>}
                      </div>)}
                </div>

                {/* Optional Order Number field */}
                {visibleFields.filter(f => f.key.includes("order")).slice(0, 1).map(field => <div key={field.id} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label htmlFor={field.id} className="block text-xs font-sans font-medium uppercase tracking-wider text-foreground">
                          {field.label}
                          {field.required && <span className="text-primary ml-1">*</span>}
                        </label>
                        {!field.required && <span className="text-[11px] text-muted-foreground font-sans lowercase">
                            optional
                          </span>}
                      </div>
                      <Input id={field.id} type="text" placeholder={field.placeholder || t.orderNumberPlaceholder} value={formValues[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} className="rounded-none bg-background border-input px-3.5 py-2.5 text-sm placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary" />
                      {fieldErrors[field.id] && <p className="text-xs text-destructive mt-1">
                          {fieldErrors[field.id].message}
                        </p>}
                    </div>)}

                {/* Subject / Topic selector */}
                {visibleFields.filter(f => f.type === "select" || f.options && f.options.length > 0).slice(0, 1).map(field => <div key={field.id} className="space-y-1.5">
                      <label htmlFor={field.id} className="block text-xs font-sans font-medium uppercase tracking-wider text-foreground">
                        {field.label}
                        {field.required && <span className="text-primary ml-1">*</span>}
                      </label>
                      <Select value={formValues[field.id] || ""} onValueChange={val => handleInputChange(field.id, val)}>
                        <SelectTrigger id={field.id} className="w-full rounded-none bg-background border-input px-3.5 py-2.5 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary">
                          <SelectValue placeholder={field.placeholder || t.inquiryTopic} />
                        </SelectTrigger>
                        <SelectContent className="rounded-none bg-popover text-popover-foreground border-border">
                          {field.options?.map((opt, optIndex) => <SelectItem key={opt.value} value={opt.value} data-index={optIndex} className="rounded-none cursor-pointer py-2 focus:bg-accent focus:text-accent-foreground">
                              {opt.label}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                      {fieldErrors[field.id] && <p className="text-xs text-destructive mt-1">
                          {fieldErrors[field.id].message}
                        </p>}
                    </div>)}

                {/* Message Textarea */}
                {visibleFields.filter(f => f.type === "textarea" || f.key.includes("message")).slice(0, 1).map(field => <div key={field.id} className="space-y-1.5">
                      <label htmlFor={field.id} className="block text-xs font-sans font-medium uppercase tracking-wider text-foreground">
                        {field.label}
                        {field.required && <span className="text-primary ml-1">*</span>}
                      </label>
                      <Textarea id={field.id} required={field.required} rows={4} placeholder={field.placeholder || t.messagePlaceholder} value={formValues[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} className="rounded-none bg-background border-input px-3.5 py-2.5 text-sm min-h-[120px] placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary resize-y" />
                      {fieldErrors[field.id] && <p className="text-xs text-destructive mt-1">
                          {fieldErrors[field.id].message}
                        </p>}
                    </div>)}

                {/* Fallback for other standard fields present in definition */}
                {visibleFields.filter(f => !f.key.includes("name") && !f.key.includes("email") && !f.key.includes("order") && !f.key.includes("topic") && !f.key.includes("message") && f.type !== "select").map(field => <div key={field.id} className="space-y-1.5">
                      <label htmlFor={field.id} className="block text-xs font-sans font-medium uppercase tracking-wider text-foreground">
                        {field.label}
                        {field.required && <span className="text-primary ml-1">*</span>}
                      </label>
                      <Input id={field.id} type="text" required={field.required} value={formValues[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} className="rounded-none bg-background border-input px-3.5 py-2.5 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary" />
                      {fieldErrors[field.id] && <p className="text-xs text-destructive mt-1">
                          {fieldErrors[field.id].message}
                        </p>}
                    </div>)}

                {/* Submit Action */}
                <div className="pt-2">
                  <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-8 py-3.5 h-auto text-xs font-sans font-semibold tracking-wider uppercase transition-colors">
                    {isSubmitting ? formDef?.submit?.processing_label || t.sendingMessage : formDef?.submit?.label || t.sendMessage}
                  </Button>
                </div>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}